/**
 * PriceTables.js
 * Módulo de gestão de tabelas de preços do estacionamento.
 * CI Sistemas de Informática Ltda. — FastALPR Parking — Fase 1
 */

import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  FiPlus, FiEdit2, FiTrash2, FiSave, FiX,
  FiDollarSign, FiClock, FiStar, FiInfo, FiCheckCircle, FiPlayCircle, FiSliders
} from 'react-icons/fi';
import '../styles/PriceTables.css';
import PriceTableRulesModal from '../components/PriceTableRulesModal';

// Formata minutos em texto legível
const formatMinutes = (min) => {
  if (!min && min !== 0) return '—';
  const m = parseInt(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest > 0 ? `${h}h ${rest}min` : `${h}h`;
};

// Formata moeda
const formatCurrency = (val) =>
  parseFloat(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Formulário vazio padrão
const EMPTY_FORM = {
  name: '',
  description: '',
  grace_period_min: 10,
  first_period_min: 60,
  first_period_price: '7.00',
  fraction_min: 60,
  fraction_price: '3.00',
  daily_max_price: '0',
  is_default: false,
};

const PriceTables = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  // Simulador
  const [simDuration, setSimDuration] = useState(90);
  const [simTableId, setSimTableId] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [rulesTable, setRulesTable] = useState(null);

  useEffect(() => { fetchTables(); }, []);

  const fetchTables = async () => {
    try {
      const res = await api.get('/api/price-tables');
      setTables(res.data.data || []);
    } catch {
      toast.error('Erro ao carregar tabelas de preços');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (table) => {
    setForm({
      name: table.name,
      description: table.description || '',
      grace_period_min: table.grace_period_min,
      first_period_min: table.first_period_min,
      first_period_price: table.first_period_price,
      fraction_min: table.fraction_min,
      fraction_price: table.fraction_price,
      daily_max_price: table.daily_max_price,
      is_default: !!table.is_default,
    });
    setEditingId(table.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Informe o nome da tabela'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/price-tables/${editingId}`, form);
        toast.success('Tabela atualizada com sucesso');
      } else {
        await api.post('/api/price-tables', form);
        toast.success('Tabela criada com sucesso');
      }
      closeForm();
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar tabela');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja desativar esta tabela de preços?')) return;
    setDeleting(id);
    try {
      await api.delete(`/api/price-tables/${id}`);
      toast.success('Tabela desativada com sucesso');
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao desativar tabela');
    } finally {
      setDeleting(null);
    }
  };

  const handleSimulate = async () => {
    if (!simDuration || simDuration < 0) { toast.error('Informe o tempo de permanência'); return; }
    setSimulating(true);
    setSimResult(null);
    try {
      const res = await api.post('/api/price-tables/simulate', {
        duration_minutes: parseInt(simDuration),
        price_table_id: simTableId || undefined,
      });
      setSimResult(res.data);
    } catch {
      toast.error('Erro ao simular cálculo');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) return <div className="pt-loading">Carregando tabelas de preços...</div>;

  return (
    <div className="price-tables-page">
      {/* ── Cabeçalho ── */}
      <div className="pt-header">
        <div>
          <h1><FiDollarSign /> Tabelas de Preços</h1>
          <p className="pt-subtitle">Configure as tarifas cobradas por tempo de permanência no estacionamento</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <FiPlus /> Nova Tabela
        </button>
      </div>

      {/* ── Lista de tabelas ── */}
      <div className="pt-grid">
        {tables.length === 0 && (
          <div className="pt-empty">
            <FiDollarSign size={48} />
            <p>Nenhuma tabela de preços configurada.</p>
            <button className="btn btn-primary" onClick={openNew}><FiPlus /> Criar Tabela</button>
          </div>
        )}
        {tables.map(table => (
          <div key={table.id} className={`pt-card ${table.is_default ? 'pt-card-default' : ''} ${!table.active ? 'pt-card-inactive' : ''}`}>
            <div className="pt-card-header">
              <div className="pt-card-title">
                {table.is_default && <span className="pt-badge-default"><FiStar /> Padrão</span>}
                {!table.active && <span className="pt-badge-inactive">Inativa</span>}
                <h3>{table.name}</h3>
                {table.description && <p className="pt-card-desc">{table.description}</p>}
              </div>
              <div className="pt-card-actions">
                <button className="btn-icon" title="Regras especiais" onClick={() => setRulesTable(table)}><FiSliders /></button>
                <button className="btn-icon" title="Editar" onClick={() => openEdit(table)}><FiEdit2 /></button>
                {!table.is_default && (
                  <button className="btn-icon btn-icon-danger" title="Desativar" onClick={() => handleDelete(table.id)} disabled={deleting === table.id}>
                    <FiTrash2 />
                  </button>
                )}
              </div>
            </div>
            <div className="pt-card-body">
              <div className="pt-row">
                <div className="pt-field">
                  <span className="pt-field-label"><FiClock /> Carência</span>
                  <span className="pt-field-value">{formatMinutes(table.grace_period_min)}</span>
                  <span className="pt-field-note">Gratuito</span>
                </div>
                <div className="pt-field">
                  <span className="pt-field-label"><FiClock /> 1º Período</span>
                  <span className="pt-field-value">{formatMinutes(table.first_period_min)}</span>
                  <span className="pt-field-note">{formatCurrency(table.first_period_price)}</span>
                </div>
                <div className="pt-field">
                  <span className="pt-field-label"><FiClock /> Fração</span>
                  <span className="pt-field-value">{formatMinutes(table.fraction_min)}</span>
                  <span className="pt-field-note">{formatCurrency(table.fraction_price)}</span>
                </div>
                <div className="pt-field pt-field-highlight">
                  <span className="pt-field-label"><FiDollarSign /> Teto Diária</span>
                  <span className="pt-field-value">{formatCurrency(table.daily_max_price)}</span>
                  <span className="pt-field-note">Máximo/dia</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Simulador de Tarifa ── */}
      <div className="pt-simulator">
        <div className="pt-simulator-header">
          <FiPlayCircle />
          <div>
            <h2>Simulador de Tarifa</h2>
            <p>Calcule o valor a pagar para um determinado tempo de permanência</p>
          </div>
        </div>
        <div className="pt-simulator-form">
          <div className="pt-sim-field">
            <label>Tempo de Permanência (minutos)</label>
            <input
              type="number"
              value={simDuration}
              onChange={e => setSimDuration(e.target.value)}
              min={0}
              step={15}
            />
          </div>
          <div className="pt-sim-field">
            <label>Tabela de Preços</label>
            <select value={simTableId} onChange={e => setSimTableId(e.target.value)}>
              <option value="">Tabela Padrão</option>
              {tables.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (Padrão)' : ''}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleSimulate} disabled={simulating}>
            <FiPlayCircle /> {simulating ? 'Calculando...' : 'Calcular'}
          </button>
        </div>
        {simResult && (
          <div className="pt-sim-result">
            <div className={`pt-sim-amount ${simResult.breakdown.in_grace_period ? 'pt-sim-free' : ''}`}>
              {simResult.breakdown.in_grace_period ? (
                <><FiCheckCircle /> <span>Dentro da carência — Gratuito</span></>
              ) : (
                <><FiDollarSign /> <span>{formatCurrency(simResult.breakdown.amount)}</span></>
              )}
            </div>
            <div className="pt-sim-breakdown">
              <p><strong>Tabela:</strong> {simResult.price_table.name}</p>
              <p><strong>Permanência:</strong> {formatMinutes(simResult.breakdown.duration_minutes)}</p>
              {!simResult.breakdown.in_grace_period && (
                <>
                  {simResult.breakdown.full_days > 0 && (
                    <p><strong>Diárias completas:</strong> {simResult.breakdown.full_days}x {formatCurrency(simResult.price_table.daily_max_price)}</p>
                  )}
                  {simResult.breakdown.first_period_applied && (
                    <p><strong>1º período:</strong> {formatMinutes(simResult.price_table.first_period_min)} = {formatCurrency(simResult.price_table.first_period_price)}</p>
                  )}
                  {simResult.breakdown.extra_fractions > 0 && (
                    <p><strong>Frações adicionais:</strong> {simResult.breakdown.extra_fractions}x {formatCurrency(simResult.price_table.fraction_price)}</p>
                  )}
                  {simResult.breakdown.daily_cap_applied && (
                    <p className="pt-sim-cap"><FiInfo /> Teto da diária aplicado: {formatCurrency(simResult.price_table.daily_max_price)}</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {rulesTable && <PriceTableRulesModal table={rulesTable} onClose={() => setRulesTable(null)} />}

      {/* ── Modal de Formulário ── */}
      {showForm && (
        <div className="pt-modal-overlay" onClick={closeForm}>
          <div className="pt-modal" onClick={e => e.stopPropagation()}>
            <div className="pt-modal-header">
              <h2>{editingId ? 'Editar Tabela de Preços' : 'Nova Tabela de Preços'}</h2>
              <button className="btn-icon" onClick={closeForm}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className="pt-form">
              <div className="pt-form-row">
                <div className="pt-form-field pt-form-field-wide">
                  <label>Nome da Tabela *</label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="Ex: Tabela Padrão, Tabela Noturna..." required />
                </div>
                <div className="pt-form-field">
                  <label>Descrição</label>
                  <input name="description" value={form.description} onChange={handleChange} placeholder="Opcional" />
                </div>
              </div>

              <div className="pt-form-section">
                <h3><FiClock /> Períodos e Valores</h3>
                <div className="pt-form-row">
                  <div className="pt-form-field">
                    <label>Carência (min)</label>
                    <input type="number" name="grace_period_min" value={form.grace_period_min} onChange={handleChange} min={0} step={5} />
                    <span className="pt-form-hint">Tempo gratuito</span>
                  </div>
                  <div className="pt-form-field">
                    <label>Duração do 1º Período (min)</label>
                    <input type="number" name="first_period_min" value={form.first_period_min} onChange={handleChange} min={1} step={15} />
                  </div>
                  <div className="pt-form-field">
                    <label>Valor do 1º Período (R$)</label>
                    <input type="number" name="first_period_price" value={form.first_period_price} onChange={handleChange} min={0} step={0.50} />
                  </div>
                </div>
                <div className="pt-form-row">
                  <div className="pt-form-field">
                    <label>Duração de cada Fração (min)</label>
                    <input type="number" name="fraction_min" value={form.fraction_min} onChange={handleChange} min={1} step={5} />
                  </div>
                  <div className="pt-form-field">
                    <label>Valor de cada Fração (R$)</label>
                    <input type="number" name="fraction_price" value={form.fraction_price} onChange={handleChange} min={0} step={0.50} />
                  </div>
                  <div className="pt-form-field">
                    <label>Teto da Diária (R$, 0 = sem teto)</label>
                    <input type="number" name="daily_max_price" value={form.daily_max_price} onChange={handleChange} min={0} step={1} />
                    <span className="pt-form-hint">Opcional: informe 0 enquanto não houver teto</span>
                  </div>
                </div>
              </div>

              <div className="pt-form-checkbox">
                <label>
                  <input type="checkbox" name="is_default" checked={form.is_default} onChange={handleChange} />
                  <span><FiStar /> Definir como tabela padrão do sistema</span>
                </label>
              </div>

              <div className="pt-form-actions">
                <button type="button" className="btn btn-outline" onClick={closeForm}><FiX /> Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <FiSave /> {saving ? 'Salvando...' : (editingId ? 'Salvar Alterações' : 'Criar Tabela')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceTables;
